/**
 * ConceptInspector — Phase 5 View 3 (catalog + graph).
 *
 * Backed by Phase 3's `/versions/{id}/inspect` endpoint, which is the only
 * source of per-concept data. The inspect payload exposes a RESOLUTION-stage
 * `concepts_index`, an `edge_type_counts` histogram, and (when populated) a
 * full `edges` list for force-directed visualization.
 *
 * URL: /admin/ontology/:clientUid/concepts?version=&q=&category=&min_cs=...
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Network,
  Search,
  ChevronUp,
  ChevronDown,
  Loader2,
  Layers,
  Info,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  pdoLifecycleApi,
  type PdoVersionInspectResponse,
  type OntologyGraph,
} from '@/services/api';
import { MaturityBadge } from '@/components/pdo/StatusBadges';
import { EmptyState } from '@/components/pdo/EmptyState';
import { ErrorBanner } from '@/components/pdo/ErrorBanner';
import {
  useResolvedClientUid,
  useFetch,
  formatNumber,
  categoryColor,
  RELATION_COLORS,
} from '@/components/pdo/utils';
import {
  OntologyGraph as OntologyGraphRenderer,
  ENTITY_TYPE_COLORS,
  REL_TYPE_COLORS,
} from '@/components/ontology/OntologyGraph';

type SortKey =
  | 'name'
  | 'category'
  | 'causal_strength'
  | 'stability'
  | 'confidence';

const SORT_LABELS: Record<SortKey, string> = {
  name: 'Name',
  category: 'Category',
  causal_strength: 'Causal',
  stability: 'Stability',
  confidence: 'Confidence',
};

/** Best-effort accessor for opaque concept index records. */
type ConceptRecord = Record<string, unknown> & { name?: string };

function getStr(c: ConceptRecord, ...keys: string[]): string | undefined {
  for (const k of keys) {
    const v = c[k];
    if (typeof v === 'string' && v) return v;
  }
  return undefined;
}
function getNum(c: ConceptRecord, ...keys: string[]): number | undefined {
  for (const k of keys) {
    const v = c[k];
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
  }
  return undefined;
}

export function ConceptInspector() {
  const clientUid = useResolvedClientUid('/admin/ontology');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const versionsQuery = useFetch(
    () =>
      clientUid
        ? pdoLifecycleApi.listVersions(clientUid, { limit: 50 })
        : Promise.resolve({ versions: [], next_cursor: null }),
    [clientUid],
  );
  const versions = versionsQuery.data?.versions ?? [];
  const currentVersionId =
    versions.find((v) => v.status === 'current')?.version_id ??
    versions[0]?.version_id ??
    null;
  const selectedVersion = searchParams.get('version') ?? currentVersionId;

  const inspect = useFetch(
    () =>
      clientUid && selectedVersion
        ? pdoLifecycleApi.inspectVersion(clientUid, selectedVersion)
        : Promise.resolve(null as unknown as PdoVersionInspectResponse),
    [clientUid, selectedVersion],
  );

  const compareTo = searchParams.get('compare_to');
  const compareInspect = useFetch(
    () =>
      clientUid && compareTo
        ? pdoLifecycleApi.inspectVersion(clientUid, compareTo)
        : Promise.resolve(null as unknown as PdoVersionInspectResponse),
    [clientUid, compareTo],
  );

  const search = searchParams.get('q') ?? '';
  const categoryFilters = useMemo(
    () => new Set(searchParams.getAll('category')),
    [searchParams],
  );
  const maturityFilters = useMemo(
    () => new Set(searchParams.getAll('maturity')),
    [searchParams],
  );
  const minCs = parseFloat(searchParams.get('min_cs') ?? '0');
  const sortKey = (searchParams.get('sort') as SortKey) ?? 'name';
  const sortDir = (searchParams.get('dir') as 'asc' | 'desc') ?? 'asc';

  const setParam = (key: string, value?: string | string[] | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(key);
        if (value == null) return next;
        const arr = Array.isArray(value) ? value : [value];
        for (const v of arr) if (v) next.append(key, v);
        return next;
      },
      { replace: true },
    );
  };

  const concepts = inspect.data?.concepts ?? [];

  const allCategories = useMemo(() => {
    const set = new Set<string>();
    for (const c of concepts) {
      const cat = getStr(c, 'category', 'concept_category');
      if (cat) set.add(cat);
    }
    return Array.from(set).sort();
  }, [concepts]);

  const filtered = useMemo(() => {
    let arr: ConceptRecord[] = concepts;
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((c) => {
        const name = getStr(c, 'name') ?? '';
        return name.toLowerCase().includes(q);
      });
    }
    if (categoryFilters.size > 0) {
      arr = arr.filter((c) => {
        const cat = getStr(c, 'category', 'concept_category');
        return cat ? categoryFilters.has(cat) : false;
      });
    }
    if (maturityFilters.size > 0) {
      arr = arr.filter((c) => {
        const m = getStr(c, 'maturity_stage');
        return m ? maturityFilters.has(m) : false;
      });
    }
    if (minCs > 0) {
      arr = arr.filter((c) => (getNum(c, 'causal_strength') ?? 0) >= minCs);
    }
    arr = [...arr].sort((a, b) => {
      const av = sortKey === 'name' || sortKey === 'category'
        ? (getStr(a, sortKey === 'name' ? 'name' : 'category', 'concept_category') ?? '')
        : (getNum(a, sortKey) ?? 0);
      const bv = sortKey === 'name' || sortKey === 'category'
        ? (getStr(b, sortKey === 'name' ? 'name' : 'category', 'concept_category') ?? '')
        : (getNum(b, sortKey) ?? 0);
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [concepts, search, categoryFilters, maturityFilters, minCs, sortKey, sortDir]);

  const compareNames = useMemo(() => {
    if (!compareInspect.data) return null;
    const set = new Set<string>();
    for (const c of compareInspect.data.concepts) {
      const n = getStr(c, 'name');
      if (n) set.add(n);
    }
    return set;
  }, [compareInspect.data]);

  const myNames = useMemo(() => {
    const set = new Set<string>();
    for (const c of concepts) {
      const n = getStr(c, 'name');
      if (n) set.add(n);
    }
    return set;
  }, [concepts]);

  const addedNames = useMemo(() => {
    if (!compareNames) return null;
    return new Set([...myNames].filter((n) => !compareNames.has(n)));
  }, [compareNames, myNames]);

  const removedFromCompare = useMemo(() => {
    if (!compareNames) return null;
    return Array.from(compareNames).filter((n) => !myNames.has(n));
  }, [compareNames, myNames]);

  if (!clientUid) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin mr-2" /> Resolving client…
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <header className="flex items-start justify-between gap-4 px-1 pb-4 border-b border-slate-200 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-teal to-brand-green flex items-center justify-center text-navy-900 shadow-glow">
            <Network size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-navy-900">Concept Catalog</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Concepts indexed during the RESOLUTION stage of{' '}
              <span className="font-mono text-navy-900">{clientUid}</span>.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedVersion ?? ''}
            onChange={(e) => setParam('version', e.target.value)}
            className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-white"
          >
            {versions.map((v) => (
              <option key={v.version_id} value={v.version_id}>
                {v.version_id} {v.status === 'current' ? '(current)' : ''}
              </option>
            ))}
            {versions.length === 0 && <option value="">No versions</option>}
          </select>
          <select
            value={compareTo ?? ''}
            onChange={(e) => setParam('compare_to', e.target.value || null)}
            className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-white"
            title="Overlay diff vs another version"
          >
            <option value="">Compare to…</option>
            {versions
              .filter((v) => v.version_id !== selectedVersion)
              .map((v) => (
                <option key={v.version_id} value={v.version_id}>
                  vs {v.version_id}
                </option>
              ))}
          </select>
          {selectedVersion && clientUid && (
            <Link
              to={`/admin/ontology/${clientUid}/explorer/${selectedVersion}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors"
              title="Open this version in graph explorer"
            >
              <Network size={12} /> Graph Explorer
            </Link>
          )}
        </div>
      </header>

      {inspect.error && (
        <ErrorBanner error={inspect.error} onRetry={inspect.refetch} />
      )}

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <section className="col-span-7 flex flex-col bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 space-y-2">
            <div className="relative">
              <Search
                size={12}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setParam('q', e.target.value || null)}
                placeholder="Search concepts..."
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <FilterChips
                label="Maturity"
                options={['candidate', 'active', 'established', 'hardened']}
                values={maturityFilters}
                onChange={(next) => setParam('maturity', Array.from(next))}
              />
              <FilterChips
                label="Category"
                options={allCategories}
                values={categoryFilters}
                onChange={(next) => setParam('category', Array.from(next))}
              />
            </div>
            <label className="flex items-center gap-2 text-[11px] text-slate-600">
              <span className="font-mono">min causal {minCs.toFixed(2)}</span>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={minCs}
                onChange={(e) =>
                  setParam(
                    'min_cs',
                    e.target.value === '0' ? null : e.target.value,
                  )
                }
                className="flex-1 accent-teal-600"
              />
            </label>
          </div>

          <div className="flex-1 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wider text-slate-500 sticky top-0 z-10">
                <tr>
                  <SortHeader
                    label={SORT_LABELS.name}
                    active={sortKey === 'name'}
                    dir={sortDir}
                    onClick={() => {
                      setParam('sort', 'name');
                      setParam(
                        'dir',
                        sortKey === 'name' && sortDir === 'asc' ? 'desc' : 'asc',
                      );
                    }}
                  />
                  <SortHeader
                    label="Type / Category"
                    active={sortKey === 'category'}
                    dir={sortDir}
                    onClick={() => {
                      setParam('sort', 'category');
                      setParam(
                        'dir',
                        sortKey === 'category' && sortDir === 'asc' ? 'desc' : 'asc',
                      );
                    }}
                  />
                  <SortHeader label="Maturity" active={false} dir={sortDir} onClick={() => undefined} />
                  <SortHeader
                    label={SORT_LABELS.causal_strength}
                    align="right"
                    active={sortKey === 'causal_strength'}
                    dir={sortDir}
                    onClick={() => {
                      setParam('sort', 'causal_strength');
                      setParam(
                        'dir',
                        sortKey === 'causal_strength' && sortDir === 'desc'
                          ? 'asc'
                          : 'desc',
                      );
                    }}
                  />
                  <SortHeader
                    label={SORT_LABELS.stability}
                    align="right"
                    active={sortKey === 'stability'}
                    dir={sortDir}
                    onClick={() => {
                      setParam('sort', 'stability');
                      setParam(
                        'dir',
                        sortKey === 'stability' && sortDir === 'desc' ? 'asc' : 'desc',
                      );
                    }}
                  />
                  <SortHeader
                    label={SORT_LABELS.confidence}
                    align="right"
                    active={sortKey === 'confidence'}
                    dir={sortDir}
                    onClick={() => {
                      setParam('sort', 'confidence');
                      setParam(
                        'dir',
                        sortKey === 'confidence' && sortDir === 'desc' ? 'asc' : 'desc',
                      );
                    }}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {inspect.loading && filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="flex items-center justify-center py-10 text-slate-400 text-xs">
                        <Loader2 size={14} className="animate-spin mr-2" />
                        Loading concepts…
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState
                        compact
                        title="No concepts match these filters"
                        hint="Adjust filters or pick a different version."
                      />
                    </td>
                  </tr>
                ) : (
                  filtered.map((c, i) => {
                    const name = getStr(c, 'name') ?? `concept-${i}`;
                    const isAdded = addedNames?.has(name) ?? false;
                    return (
                      <ConceptRow
                        key={`${name}-${i}`}
                        concept={c}
                        isAdded={isAdded}
                        onClick={() => {
                          navigate(
                            `/admin/ontology/${clientUid}/concepts/${encodeURIComponent(
                              name,
                            )}?version=${selectedVersion}`,
                          );
                        }}
                      />
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <footer className="px-3 py-1.5 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500 flex items-center justify-between">
            <span>
              {formatNumber(filtered.length)} of {formatNumber(concepts.length)} concepts
              {compareNames && (
                <span className="ml-2 text-emerald-700">
                  · {addedNames?.size ?? 0} added
                </span>
              )}
              {compareNames && removedFromCompare && removedFromCompare.length > 0 && (
                <span className="ml-2 text-red-700">
                  · {removedFromCompare.length} removed
                </span>
              )}
            </span>
            <Link
              to={`/admin/ontology/${clientUid}/builds`}
              className="font-medium text-teal-700 hover:underline"
            >
              View build →
            </Link>
          </footer>
        </section>

        <section className="col-span-5 flex flex-col bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50">
            <Network size={13} className="text-slate-400" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Causal Graph
            </span>
          </div>
          <div className="flex-1 flex flex-col min-h-0">
            <ConceptGraph inspect={inspect.data} />
            {compareNames && removedFromCompare && removedFromCompare.length > 0 && (
              <div className="p-3 border-t border-slate-100">
                <RemovedConcepts names={removedFromCompare} />
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function ConceptGraph({ inspect }: { inspect: PdoVersionInspectResponse | null }) {
  const graphData = useMemo<OntologyGraph | null>(() => {
    if (!inspect) return null;
    const concepts = inspect.concepts ?? [];
    const edges = inspect.edges ?? [];
    if (!concepts.length && !edges.length) return null;

    // Map concepts → OntologyNodeData (fill required fields with sensible defaults)
    const nodes = concepts.map((c) => {
      const et = String(c.entity_type ?? 'OTHER').toUpperCase();
      return {
        id: String(c.id ?? (c.name as string ?? '').toLowerCase().replace(/\s+/g, '_')),
        name: String(c.name ?? ''),
        entity_type: et as 'ATTRIBUTE' | 'NEED' | 'VALUE' | 'TRUST' | 'OTHER',
        category: String(c.category ?? 'unknown'),
        status: String(c.status ?? 'active'),
        color: ENTITY_TYPE_COLORS[et] ?? '#94a3b8',
        body: '',
        causal_strength: Number(c.causal_strength ?? 0),
        causal_stability: Number(c.causal_stability ?? 0),
        cognitive_mechanism: '',
        persona_activations: {},
        version_introduced: null,
      };
    });

    // Build a lookup of node ids so we can fall back to deriving ids from the
    // edge source/target bare_ids if concepts were keyed differently.
    const nodeIds = new Set(nodes.map((n) => n.id));

    // Edges are already in the right shape; fill missing color/style/label.
    const mappedEdges = edges.map((e) => {
      const rel = String(e.rel_type ?? '').toUpperCase();
      return {
        id: e.id ?? `${e.source}__${rel}__${e.target}`,
        source: e.source,
        target: e.target,
        rel_type: rel,
        weight: Number(e.weight ?? 1),
        evidence_count: Number(e.evidence_count ?? 0),
        cycle: null,
        color: (REL_TYPE_COLORS as Record<string, string>)[rel] ?? '#94a3b8',
        style: 'solid' as const,
        label: e.label ?? rel,
      };
    }).filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));

    return {
      nodes,
      edges: mappedEdges,
      meta: {
        version: inspect.version_id,
        node_count: nodes.length,
        edge_count: mappedEdges.length,
        entity_types: [...new Set(nodes.map((n) => n.entity_type))],
        categories: [...new Set(nodes.map((n) => n.category))],
        rel_types: [...new Set(mappedEdges.map((e) => e.rel_type))],
      },
    };
  }, [inspect]);

  const hasGraph = graphData && graphData.edges.length > 0;
  const edgeCounts = inspect?.edge_type_counts ?? {};
  const histogramEntries = Object.entries(edgeCounts).sort((a, b) => b[1] - a[1]);
  const histogramMax = Math.max(...histogramEntries.map(([, n]) => n), 1);

  if (!inspect) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <EmptyState compact title="No version selected" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {hasGraph ? (
        <>
          {/* Force-directed Sigma graph */}
          <div className="relative flex-1 min-h-0" style={{ minHeight: 260 }}>
            <OntologyGraphRenderer graph={graphData} />
          </div>
          {/* Compact legend / histogram strip */}
          {histogramEntries.length > 0 && (
            <div className="border-t border-slate-100 px-3 py-2 bg-slate-50 flex flex-wrap gap-x-4 gap-y-1">
              {histogramEntries.map(([rel, count]) => (
                <div key={rel} className="flex items-center gap-1.5 text-[11px] text-slate-600">
                  <span
                    className="w-2.5 h-1.5 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor:
                        (REL_TYPE_COLORS as Record<string, string>)[rel] ?? '#94a3b8',
                    }}
                  />
                  <span className="font-mono">{rel}</span>
                  <span className="font-semibold text-slate-800">{formatNumber(count)}</span>
                  <div className="w-12 h-1 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${(count / histogramMax) * 100}%`,
                        backgroundColor:
                          (REL_TYPE_COLORS as Record<string, string>)[rel] ?? '#94a3b8',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Histogram when graph isn't available yet */}
          {histogramEntries.length > 0 ? (
            <div className="space-y-1.5">
              {histogramEntries.map(([rel, count]) => (
                <div key={rel} className="flex items-center gap-2 text-xs">
                  <span
                    className="w-3 h-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: RELATION_COLORS[rel] ?? '#cbd5e1' }}
                  />
                  <span className="font-mono text-slate-700 w-32 truncate">{rel}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full"
                      style={{
                        width: `${(count / histogramMax) * 100}%`,
                        backgroundColor: RELATION_COLORS[rel] ?? '#94a3b8',
                      }}
                    />
                  </div>
                  <span className="font-mono text-slate-700 w-12 text-right">
                    {formatNumber(count)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              compact
              title="No edge histogram recorded"
              hint="The CAUSAL stage didn't write edge type counts for this version."
            />
          )}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800 flex items-start gap-2">
            <Info size={13} className="flex-shrink-0 mt-0.5" />
            <p>
              Causal edges aren&apos;t available for this version. The force-directed graph
              will appear here once the{' '}
              <code className="font-mono">inspect</code> endpoint returns an edge list
              (populated automatically via the OntologyProvider or a fresh build).
            </p>
          </div>
        </div>
      )}
    </div>
  );
}



function RemovedConcepts({ names }: { names: string[] }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3">
      <p className="text-[10px] uppercase tracking-wider font-semibold text-red-700 mb-1">
        Removed vs comparison ({names.length})
      </p>
      <ul className="space-y-0.5 text-xs text-red-800 max-h-40 overflow-y-auto">
        {names.slice(0, 40).map((n) => (
          <li key={n} className="font-mono truncate">
            − {n}
          </li>
        ))}
        {names.length > 40 && (
          <li className="text-red-700/70 italic">+{names.length - 40} more</li>
        )}
      </ul>
    </div>
  );
}

function SortHeader({
  label,
  active,
  dir,
  align = 'left',
  onClick,
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  align?: 'left' | 'right';
  onClick: () => void;
}) {
  return (
    <th
      onClick={onClick}
      className={clsx(
        'px-3 py-2 cursor-pointer select-none',
        align === 'right' ? 'text-right' : 'text-left',
        active && 'text-navy-900',
      )}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (dir === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}
      </span>
    </th>
  );
}

function ConceptRow({
  concept,
  isAdded,
  onClick,
}: {
  concept: ConceptRecord;
  isAdded: boolean;
  onClick: () => void;
}) {
  const name = getStr(concept, 'name') ?? '–';
  const entityType = getStr(concept, 'entity_type', 'type') ?? '';
  const category = getStr(concept, 'category', 'concept_category') ?? '';
  const maturity = getStr(concept, 'maturity_stage');
  const causalStrength = getNum(concept, 'causal_strength');
  const stability = getNum(concept, 'stability', 'avg_stability');
  const confidence = getNum(concept, 'confidence');

  return (
    <tr
      onClick={onClick}
      className={clsx(
        'hover:bg-slate-50 cursor-pointer transition-colors',
        isAdded && 'bg-emerald-50/40',
      )}
    >
      <td className="px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          {category && (
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: categoryColor(category) }}
            />
          )}
          <span className="font-medium text-navy-900 truncate">{name}</span>
        </div>
      </td>
      <td className="px-3 py-2 text-slate-600">
        <p className="text-[11px] text-slate-700">{category || '–'}</p>
        {entityType && (
          <p className="text-[10px] font-mono text-slate-400">{entityType}</p>
        )}
      </td>
      <td className="px-3 py-2">
        {maturity ? <MaturityBadge stage={maturity} /> : <span className="text-slate-400">–</span>}
      </td>
      <td className="px-3 py-2 text-right">
        <Bar value={causalStrength} />
      </td>
      <td className="px-3 py-2 text-right">
        <Bar value={stability} />
      </td>
      <td className="px-3 py-2 text-right">
        <Bar value={confidence} />
      </td>
    </tr>
  );
}

function Bar({ value }: { value: number | undefined }) {
  if (value === undefined) {
    return <span className="text-[10px] text-slate-400">–</span>;
  }
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className="inline-flex items-center gap-1.5">
      <div className="w-12 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className="h-full bg-teal-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-[10px] text-slate-700">{value.toFixed(2)}</span>
    </div>
  );
}

function FilterChips<T extends string>({
  label,
  options,
  values,
  onChange,
}: {
  label: string;
  options: T[];
  values: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          'inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium rounded border',
          values.size > 0
            ? 'bg-teal-50 border-teal-200 text-teal-700'
            : 'bg-white border-slate-200 text-slate-600',
        )}
      >
        <Layers size={11} />
        {label}
        {values.size > 0 && <span className="text-teal-600">·{values.size}</span>}
      </button>
      {open && (
        <div className="absolute z-20 mt-1 left-0 bg-white border border-slate-200 rounded-lg shadow-lg p-2 min-w-[160px]">
          <div className="space-y-1 max-h-56 overflow-y-auto">
            {options.length === 0 ? (
              <p className="text-[11px] text-slate-400 px-1">No options</p>
            ) : (
              options.map((opt) => {
                const checked = values.has(opt);
                return (
                  <label
                    key={opt}
                    className="flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer hover:bg-slate-50 px-1.5 py-0.5 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(values);
                        if (checked) next.delete(opt);
                        else next.add(opt);
                        onChange(next);
                      }}
                      className="w-3 h-3 accent-teal-600"
                    />
                    {opt}
                  </label>
                );
              })
            )}
          </div>
          {values.size > 0 && (
            <button
              type="button"
              onClick={() => onChange(new Set())}
              className="mt-1 w-full text-[10px] text-slate-500 hover:text-slate-700"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}
